import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { ticketsAPI, projectsAPI } from '../api/api';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

const ProjectStatusManagement = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { projects } = useProject();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    status_name: '',
    order_index: 0,
    show_on_board: true
  });
  const [project, setProject] = useState(null);

  // Proje bilgisini yükle
  useEffect(() => {
    if (projectId) {
      // Önce context'ten projeyi bul
      if (projects && Array.isArray(projects)) {
        const foundProject = projects.find(p => p.id === parseInt(projectId));
        if (foundProject) {
          setProject(foundProject);
          return;
        }
      }

      // Context'te yoksa API'den yükle
      loadProjectFromAPI();
    }
  }, [projectId, projects]);

  const loadProjectFromAPI = async () => {
    try {
      const response = await projectsAPI.getProjects();
      if (response?.success && response?.data) {
        const foundProject = response.data.find(p => p.id === parseInt(projectId));
        if (foundProject) {
          setProject(foundProject);
        }
      }
    } catch (error) {
      // Silently handle error
    }
  };

  // Status'ları yükle
  useEffect(() => {
    if (projectId) {
      loadStatuses();
    }
  }, [projectId]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ticketsAPI.getStatusesByProject(projectId);
      if (response?.success) {
        const statusList = Array.isArray(response.data) ? response.data : [];
        setStatuses(statusList.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
      } else {
        setError(response?.message || 'Status\'lar yüklenirken hata oluştu');
      }
    } catch (error) {
      // Silently handle error
      setError('Status\'lar yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStatus = () => {
    setFormData({
      status_name: '',
      order_index: statuses.length > 0 ? Math.max(...statuses.map(s => s.order_index || 0)) + 1 : 0,
      show_on_board: true
    });
    setEditingStatus(null);
    setShowAddModal(true);
  };

  const handleEditStatus = (status) => {
    setFormData({
      status_name: status.status_name,
      order_index: status.order_index || 0,
      show_on_board: status.show_on_board !== false // default true
    });
    setEditingStatus(status);
    setShowAddModal(true);
  };

  const handleDeleteStatus = async (status) => {
    if (!window.confirm(`"${status.status_name}" status'ünü silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await ticketsAPI.deleteStatus(status.id);
      if (response?.success) {
        await loadStatuses();
      } else {
        alert(response?.message || 'Status silinirken hata oluştu');
      }
    } catch (error) {
      // Silently handle error
      alert('Status silinirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!formData.status_name.trim()) {
      alert('Status adı gereklidir');
      return;
    }

    try {
      setLoading(true);
      const statusData = {
        ...formData,
        project_id: parseInt(projectId),
        order_index: parseInt(formData.order_index) || 0
      };

      let response;
      if (editingStatus) {
        // Güncelle
        response = await ticketsAPI.updateStatus({
          id: editingStatus.id,
          ...statusData
        });
      } else {
        // Yeni oluştur
        response = await ticketsAPI.createStatus(statusData);
      }

      if (response?.success) {
        setShowAddModal(false);
        setEditingStatus(null);
        setFormData({ status_name: '', order_index: 0 });
        await loadStatuses();
      } else {
        alert(response?.message || 'Status kaydedilirken hata oluştu');
      }
    } catch (error) {
      // Silently handle error
      alert('Status kaydedilirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveStatus = async (status, direction) => {
    const currentIndex = statuses.findIndex(s => s.id === status.id);
    if (currentIndex === -1) return;

    let targetIndex;
    if (direction === 'up' && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < statuses.length - 1) {
      targetIndex = currentIndex + 1;
    } else {
      return;
    }

    const targetStatus = statuses[targetIndex];
    const newOrderIndex = targetStatus.order_index || targetIndex;
    const oldOrderIndex = status.order_index || currentIndex;

    try {
      setLoading(true);
      // İki status'un order_index'lerini değiştir
      await ticketsAPI.updateStatus({
        id: status.id,
        status_name: status.status_name,
        project_id: parseInt(projectId),
        order_index: newOrderIndex
      });
      await ticketsAPI.updateStatus({
        id: targetStatus.id,
        status_name: targetStatus.status_name,
        project_id: parseInt(projectId),
        order_index: oldOrderIndex
      });
      await loadStatuses();
    } catch (error) {
      // Silently handle error
      alert('Status sırası değiştirilirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !statuses.length) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/projects')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Proje Status Yönetimi
              </h1>
              {project && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {project.project_name} ({project.project_code})
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleAddStatus}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Yeni Status</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Status List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {statuses.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Henüz hiç status oluşturulmamış.
              </p>
              <button
                onClick={handleAddStatus}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                İlk Status'ü Oluştur
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {statuses.map((status, index) => (
                <div
                  key={status.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveStatus(status, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMoveStatus(status, 'down')}
                        disabled={index === statuses.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {status.status_name}
                        </h3>
                        {!status.show_on_board && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-xs rounded-full">
                            Board'da Gizli
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Sıra: {status.order_index !== null && status.order_index !== undefined ? status.order_index : index}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditStatus(status)}
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Düzenle"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteStatus(status)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingStatus ? 'Status Düzenle' : 'Yeni Status Oluştur'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status Adı *
                </label>
                <input
                  type="text"
                  value={formData.status_name}
                  onChange={(e) => setFormData({ ...formData, status_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn: Beklemede, Yapılıyor, Bitti"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sıra (Order Index)
                </label>
                <input
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Status'ların görüntülenme sırası (düşük sayı önce gösterilir)
                </p>
              </div>
              <div className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  id="show_on_board"
                  checked={formData.show_on_board}
                  onChange={(e) => setFormData({ ...formData, show_on_board: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 bg-white dark:bg-gray-700"
                />
                <label htmlFor="show_on_board" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Board üzerinde göster
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingStatus(null);
                  setFormData({ status_name: '', order_index: 0 });
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSaveStatus}
                disabled={loading || !formData.status_name.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Kaydediliyor...' : editingStatus ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectStatusManagement;

